import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import { IconCopy, IconDownload, IconBarChart, IconFileText, IconRefresh, IconSettings, IconCpu, IconCheckCircle } from './Icons';

const ExportScreen = ({ projectData, onBack, onNewProject, onError, apiBaseUrl, onGoToKernelBuilder }) => {
  const [exportFormat, setExportFormat] = useState('excel');
  const [includeDetails, setIncludeDetails] = useState(true);
  const [includeVisualization, setIncludeVisualization] = useState(true);
  const [multiAnswerMode, setMultiAnswerMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [visualizationUrl, setVisualizationUrl] = useState(null);
  const [classificationStats, setClassificationStats] = useState(null);
  const [plotData, setPlotData] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);

  useEffect(() => {
    loadClassificationStats();
    generateVisualization();
    
    // Cleanup blob URL при размонтировании компонента
    return () => {
      if (visualizationUrl && visualizationUrl.startsWith('blob:')) {
        URL.revokeObjectURL(visualizationUrl);
      }
    };
  }, []);

  const loadClassificationStats = async () => {
    try {
      // Получаем статистику классификации
      const projectResponse = await axios.get(
        `${apiBaseUrl}/api/project/${projectData.project_id}`
      );
      
      if (projectResponse.data.project?.categories) {
        const stats = calculateStats(projectResponse.data.project.categories);
        setClassificationStats(stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const calculateStats = (categories) => {
    const stats = {
      totalCategories: 0,
      totalTexts: projectData.text_count || projectData.project?.text_count || 0,
      distribution: [],
      largestCategory: null,
      smallestCategory: null
    };

    let maxSize = 0;
    let minSize = Infinity;
    const clustersSource = projectData.clusters || projectData.project?.clusters || {};
    
    // Фильтруем только объекты категорий (у которых есть поле clusters)
    const categoryList = Object.values(categories || {}).filter(cat => cat && Array.isArray(cat.clusters));
    stats.totalCategories = categoryList.length;

    categoryList.forEach(category => {
      const textCount = category.clusters.reduce((total, clusterId) => {
        const cluster = clustersSource[clusterId];
        return total + (cluster?.size || 0);
      }, 0);

      const percentage = ((textCount / stats.totalTexts) * 100).toFixed(1);
      
      stats.distribution.push({
        name: category.name,
        count: textCount,
        percentage: parseFloat(percentage),
        color: category.color
      });

      if (textCount > maxSize) {
        maxSize = textCount;
        stats.largestCategory = {
          name: category.name,
          count: textCount
        };
      }
      
      if (textCount < minSize && textCount > 0) {
        minSize = textCount;
        stats.smallestCategory = {
          name: category.name,
          count: textCount
        };
      }
    });

    // Сортируем по количеству текстов
    stats.distribution.sort((a, b) => b.count - a.count);

    return stats;
  };

  const generateVisualization = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/project/${projectData.project_id}/visualize`
      );
      
      if (response.data.success) {
        setPlotData(response.data.data);
      }
    } catch (err) {
      console.error('Error generating visualization:', err);
      // Fallback к старому методу с PNG
      try {
        const response = await axios.get(
          `${apiBaseUrl}/api/project/${projectData.project_id}/visualize`,
          { responseType: 'blob' }
        );
        const imageUrl = URL.createObjectURL(response.data);
        setVisualizationUrl(imageUrl);
      } catch (err2) {
        setVisualizationUrl(null);
      }
    }
  };

  const handlePlotClick = (event) => {
    if (event.points && event.points.length > 0) {
      const point = event.points[0];
      const clusterName = point.data.name;
      const text = point.data.text[point.pointNumber];
      setSelectedPoint({ cluster: clusterName, text: text });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/project/${projectData.project_id}/export`,
        {
          format: exportFormat,
          include_details: includeDetails,
          include_visualization: includeVisualization,
          multi_answer_mode: multiAnswerMode
        },
        {
          responseType: 'blob'
        }
      );

      // Определяем имя файла из заголовков или используем дефолтное
      const contentDisposition = response.headers['content-disposition'];
      const projectName = projectData.project?.name || projectData.project_name || 'project';
      // Очищаем имя проекта от недопустимых символов для файловой системы
      const safeProjectName = projectName.replace(/[<>:"/\\|?*]/g, '_').trim();
      let filename = `${safeProjectName}_results.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      onError(err.response?.data?.error || 'Ошибка при экспорте данных');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadVisualization = () => {
    if (visualizationUrl) {
      const link = document.createElement('a');
      link.href = visualizationUrl;
      link.setAttribute('download', `visualization_${projectData.project_id}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleCopySummary = () => {
    if (classificationStats) {
      const summaryText = `Результаты анализа проекта "${projectData.project?.name || 'Без названия'}"\n\n` +
        `Общая статистика:\n` +
        `• Всего текстов: ${classificationStats.totalTexts}\n` +
        `• Категорий: ${classificationStats.totalCategories}\n` +
        `• Самая большая категория: ${classificationStats.largestCategory?.name || 'нет'} (${classificationStats.largestCategory?.count || 0} текстов)\n` +
        `• Самая маленькая категория: ${classificationStats.smallestCategory?.name || 'нет'} (${classificationStats.smallestCategory?.count || 0} текстов)\n\n` +
        `Распределение:\n` +
        classificationStats.distribution.map(cat => 
          `• ${cat.name}: ${cat.count} текстов (${cat.percentage}%)`
        ).join('\n');
      
      navigator.clipboard.writeText(summaryText)
        .then(() => alert('Сводка скопирована в буфер обмена!'))
        .catch(err => console.error('Copy failed:', err));
    }
  };

  return (
    <div className="export-screen">
      <div className="export-header">
        <h2>4. Экспорт результатов</h2>
        <p className="subtitle">
          Ваш анализ завершен! Экспортируйте результаты в нужном формате.
        </p>
      </div>

      <div className="export-content">
        {/* Левая колонка - статистика и визуализация */}
        <div className="stats-visualization">
          <div className="project-summary-card">
            <h3>Сводка проекта</h3>
            <div className="project-info">
              <div className="info-row">
                <span className="info-label">Название:</span>
                <span className="info-value">{projectData.project?.name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Текстов:</span>
                <span className="info-value">{projectData.text_count || projectData.project?.text_count}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Кластеров:</span>
                <span className="info-value">{projectData.n_clusters || projectData.project?.n_clusters}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Категорий:</span>
                <span className="info-value">
                  {Object.values(projectData.categories || {}).filter(cat => cat && Array.isArray(cat.clusters)).length}
                </span>
              </div>
            </div>
          </div>

          {classificationStats && (
            <div className="stats-card">
              <div className="stats-header">
                <h3>Статистика классификации</h3>
                <button 
                  className="copy-summary-btn"
                  onClick={handleCopySummary}
                  title="Копировать сводку"
                >
                  <IconCopy size={14} style={{ marginRight: '5px', marginTop: '-2px' }} />Копировать
                </button>
              </div>
              
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">{classificationStats.totalTexts}</div>
                  <div className="stat-label">всего текстов</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{classificationStats.totalCategories}</div>
                  <div className="stat-label">категорий</div>
                </div>
                <div className="stat-item large-category">
                  <div className="stat-number">
                    {classificationStats.largestCategory ? 
                      classificationStats.largestCategory.count : '—'}
                  </div>
                  <div className="stat-label">
                    {classificationStats.largestCategory ? (
                      <>
                        <strong>{classificationStats.largestCategory.name}</strong>
                        <span className="stat-hint">(самая большая категория)</span>
                      </>
                    ) : 'нет данных'}
                  </div>
                </div>
                <div className="stat-item small-category">
                  <div className="stat-number">
                    {classificationStats.smallestCategory ? 
                      classificationStats.smallestCategory.count : '—'}
                  </div>
                  <div className="stat-label">
                    {classificationStats.smallestCategory ? (
                      <>
                        <strong>{classificationStats.smallestCategory.name}</strong>
                        <span className="stat-hint">(самая маленькая категория)</span>
                      </>
                    ) : 'нет данных'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {(plotData || visualizationUrl) && (
            <div className="visualization-card">
              <div className="viz-header">
                <h3>{plotData?.has_categories !== false ? 'Визуализация категорий' : 'Визуализация кластеров'}</h3>
                {visualizationUrl && (
                  <button 
                    className="download-viz-btn"
                    onClick={handleDownloadVisualization}
                  >
                    <IconDownload size={14} style={{ marginRight: '5px', marginTop: '-2px' }} />Скачать
                  </button>
                )}
              </div>
              <div className="viz-preview">
                {plotData ? (
                  <div className="viz-plotly">
                    <Plot
                      data={plotData.map(category => ({
                        x: category.x,
                        y: category.y,
                        mode: 'markers',
                        type: 'scatter',
                        name: category.category_name,
                        text: category.texts,
                        hovertemplate: 
                          '<b>%{data.name}</b><br>' +
                          'Текст: %{text}<br>' +
                          '<extra></extra>',
                        marker: {
                          color: category.color,
                          size: 10,
                          opacity: 0.7,
                          line: {
                            color: 'white',
                            width: 1
                          }
                        }
                      }))}
                      layout={{
                        autosize: true,
                        margin: { l: 40, r: 40, t: 40, b: 40 },
                        font: { color: '#33405e' },
                        xaxis: { 
                          title: 't-SNE компонента 1',
                          zeroline: false,
                          showgrid: true,
                          gridcolor: '#e6eaf2',
                          linecolor: '#d4dbe8',
                          tickfont: { color: '#33405e' }
                        },
                        yaxis: { 
                          title: 't-SNE компонента 2',
                          zeroline: false,
                          showgrid: true,
                          gridcolor: '#e6eaf2',
                          linecolor: '#d4dbe8',
                          tickfont: { color: '#33405e' }
                        },
                        legend: {
                          orientation: 'h',
                          y: -0.15,
                          x: 0.5,
                          xanchor: 'center',
                          bgcolor: '#ffffff',
                          font: { color: '#33405e' }
                        },
                        hovermode: 'closest',
                        paper_bgcolor: '#ffffff',
                        plot_bgcolor: '#ffffff'
                      }}
                      config={{
                        responsive: true,
                        displayModeBar: true,
                        modeBarButtonsToRemove: ['lasso2d', 'select2d']
                      }}
                      style={{ width: '100%', height: '100%' }}
                      onClick={handlePlotClick}
                    />
                    {selectedPoint && (
                      <div className="selected-point-info">
                        <strong>{selectedPoint.cluster}</strong>
                        <p>{selectedPoint.text}</p>
                        <button onClick={() => setSelectedPoint(null)}>✕</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <img 
                    src={visualizationUrl} 
                    alt="Визуализация кластеров" 
                    className="viz-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = 
                        '<div class="viz-placeholder">Визуализация временно недоступна</div>';
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Правая колонка - настройки экспорта */}
        <div className="export-settings">
          <div className="settings-card">
            <h3>Настройки экспорта</h3>

            <div className="setting-group">
              <label className="setting-label">Формат файла:</label>
              <div className="format-options">
                <label className="format-option">
                  <input
                    type="radio"
                    value="excel"
                    checked={exportFormat === 'excel'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  <div className="option-content">
                    <div className="option-icon"><IconBarChart size={26} /></div>
                    <div className="option-text">
                      <div className="option-title">Excel (.xlsx)</div>
                      <div className="option-description">
                        Подходит для дальнейшего анализа в Excel
                      </div>
                    </div>
                  </div>
                </label>

                <label className="format-option">
                  <input
                    type="radio"
                    value="csv"
                    checked={exportFormat === 'csv'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  <div className="option-content">
                    <div className="option-icon"><IconFileText size={26} /></div>
                    <div className="option-text">
                      <div className="option-title">CSV</div>
                      <div className="option-description">
                        Универсальный формат, подходит для импорта в другие системы
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="setting-group">
              <label className="setting-label">Дополнительные опции:</label>
              
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={includeDetails}
                  onChange={(e) => setIncludeDetails(e.target.checked)}
                />
                <span className="checkbox-label">
                  Включить детальную информацию
                  <span className="option-hint">(ключевые слова, примеры текстов)</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={includeVisualization}
                  onChange={(e) => setIncludeVisualization(e.target.checked)}
                />
                <span className="checkbox-label">
                  Включить визуализацию
                  <span className="option-hint">(диаграмму кластеров в отдельном листе)</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={multiAnswerMode}
                  onChange={(e) => setMultiAnswerMode(e.target.checked)}
                />
                <span className="checkbox-label">
                  Multi-answer режим
                  <span className="option-hint">(коды категорий по колонкам Q1, Q2, Q3)</span>
                </span>
              </label>
            </div>

            <div className="setting-group">
              <label className="setting-label">Предпросмотр данных:</label>
              <div className="data-preview">
                <div className="preview-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Текст</th>
                        <th>Кластер</th>
                        <th>Категория</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const clustersSource = projectData.clusters || projectData.project?.clusters || {};
                        const categoryList = Object.values(projectData.categories || {}).filter(cat => cat && Array.isArray(cat.clusters));
                        
                        return Object.entries(clustersSource)
                          .slice(0, 5)
                          .map(([clusterId, clusterData]) => (
                            <tr key={clusterId}>
                              <td className="text-preview">
                                {clusterData.examples?.[0]?.slice(0, 50)}...
                              </td>
                              <td className="cluster-id">Кластер {parseInt(clusterId) + 1}</td>
                              <td className="category-name">
                                {categoryList.find(cat => 
                                  cat.clusters.includes(parseInt(clusterId))
                                )?.name || 'Не назначено'}
                              </td>
                            </tr>
                          ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="export-actions">
              <button
                className="export-button"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner"></span>
                    Экспорт...
                  </>
                ) : (
                  <>
                    <span className="export-icon"><IconDownload size={17} /></span>
                    Экспортировать результаты
                  </>
                )}
              </button>

              <div className="export-hint">
                Файл будет скачан автоматически после обработки
              </div>
            </div>
          </div>

          <div className="next-steps-card">
            <h3>Что дальше?</h3>
            <div className="next-steps">
              <div className="step">
                <div className="step-icon"><IconRefresh size={24} /></div>
                <div className="step-content">
                  <div className="step-title">Новый анализ</div>
                  <div className="step-description">
                    Загрузите другой файл для анализа
                  </div>
                  <button 
                    className="step-action"
                    onClick={onNewProject}
                  >
                    Начать новый проект
                  </button>
                </div>
              </div>

              <div className="step">
                <div className="step-icon"><IconSettings size={24} /></div>
                <div className="step-content">
                  <div className="step-title">Настроить заново</div>
                  <div className="step-description">
                    Вернитесь к редактированию категорий
                  </div>
                  <button 
                    className="step-action secondary"
                    onClick={onBack}
                  >
                    Вернуться к редактированию
                  </button>
                </div>
              </div>

              <div className="step">
                <div className="step-icon"><IconCpu size={24} /></div>
                <div className="step-content">
                  <div className="step-title">Улучшить ядра</div>
                  <div className="step-description">
                    ИИ найдёт новые ключевые слова в ответах респондентов
                  </div>
                  <button 
                    className="step-action"
                    onClick={onGoToKernelBuilder}
                    style={{background: '#007bff'}}
                  >
                    Разработчик ядра ИИ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="export-footer">
        <div className="completion-message">
          <div className="completion-icon" style={{ color: 'var(--cyan)' }}><IconCheckCircle size={40} /></div>
          <div className="completion-text">
            <h3>Анализ завершен успешно!</h3>
            <p>Результаты готовы к экспорту. Вы можете сохранить их для дальнейшего использования.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportScreen;
